"use client";

import React from "react";
import { motion } from "framer-motion";
import { stegaClean } from "@sanity/client/stega";

import Image from "next/image";
import Chip from "@/components/global/ui/Chip";
import BackgroundVideo from "@/components/global/utils/BackgroundVideo";
import HeroMobilePreview from "@/components/lp-long/HeroMobilePreview";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTACornerButton } from "@/shadcn/ui/corner-button";

import { PageHomeNewSchema } from "@/sanity/pages/PageHomeNewTemplate";

import heroGrid from "@/public/home-new/hero-grid.svg";
import fallbackLights from "@/public/home-new/hero-fallback.png";
import heroAssetDesktopDe from "@/public/home-new/hero_asset_desktop_de.jpg";
import heroAssetDesktopEn from "@/public/home-new/hero_asset_desktop_en.jpg";

export default function HeroSection({ data, className }: { data: PageHomeNewSchema; className?: string }) {
  return (
    <section className={cn("relative overflow-clip pt-[8.25rem] lg:pt-[15.5rem] pb-[8rem] lg:pb-[12rem]", className)}>
      <div className="pointer-events-none absolute top-[2rem] left-1/2 h-[109rem] w-[min(226rem,calc(100%-4rem))] -translate-x-1/2 overflow-clip rounded-[2rem] md:rounded-[4rem] lg:top-[1rem] lg:h-[118rem]">
        <div className="relative h-full w-full">
          <div className="absolute inset-0 bg-gradient-to-b from-[#EFFFDF] to-[#D9F9B9] opacity-70" />
          <Image
            className="absolute top-0 left-1/2 !h-full !w-[200rem] !max-w-none -translate-x-1/2 object-cover lg:!w-[max(243.25rem,100%)]"
            src={heroGrid}
            alt="grid background"
          />
        </div>
      </div>

      <div
        className="pointer-events-none absolute top-[2rem] left-1/2 z-10 h-[109rem] w-[min(226rem,calc(100%-4rem))] -translate-x-1/2 overflow-clip rounded-[2rem] opacity-[.25] md:rounded-[4rem] lg:top-[1rem] lg:h-[118rem]"
        style={{
          maskImage: "linear-gradient(to bottom, black 0%, black 50%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 50%, transparent 100%)",
        }}>
        <BackgroundVideo
          playbackId={"Ri02q97kDI88T3I8Fpss8XTZqILxOt01TbAAuMNNOwO02o"}
          fallbackImage={fallbackLights}
        />
      </div>

      <div className="relative z-20 container flex flex-col items-center">
        <div className="flex flex-col items-center px-[2rem] lg:px-0">
          <Chip
            className="prose-img:!w-[3rem]"
            image={data?.heroChip?.icon}
            text={data?.heroChip?.text}
          />

          {data?.heroTitle && (
            <div className="richtext prose-p:w-[min(92.5rem,100%)] prose-headings:w-[min(112.5rem,100%)]">
              <PortableText
                value={data.heroTitle}
                components={richTextComponent}
              />
            </div>
          )}

          {data?.heroCTA && (
            <CTACornerButton
              className="mt-[2rem]"
              data={data.heroCTA}
            />
          )}
        </div>
      </div>

      <div className="relative container flex flex-col items-center">
        <div className="relative mt-[7.75rem] w-[calc(100%+2rem)] lg:mt-[10.75rem] lg:w-[189.75rem]">
          <motion.div
            initial={{ opacity: 0, y: 20, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            transition={{ duration: 0.5, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -top-[2.75rem] left-1/2 h-1/2 w-[calc(100%-16rem)] -translate-x-1/2 rounded-[2rem] bg-[#000E0A]/20 backdrop-blur-[2rem] lg:-top-[5.5rem] lg:w-[calc(100%-54rem)] lg:rounded-[4rem]"
          />
          <motion.div
            initial={{ opacity: 0, y: 20, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            transition={{ duration: 0.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -top-[1.75rem] left-1/2 h-1/2 w-[calc(100%-9rem)] -translate-x-1/2 rounded-[2rem] bg-[#000E0A]/20 backdrop-blur-[2rem] lg:-top-[3rem] lg:w-[calc(100%-28rem)] lg:rounded-[4rem]"
          />

          <motion.div
            initial={{ opacity: 0, rotateX: 50, scale: 0.95 }}
            animate={{ opacity: 1, rotateX: 0, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: "bottom" }}
            className="rounded-[2.5rem] border border-white/[.24] bg-[#000E0A]/40 p-[.5rem] backdrop-blur-[2rem] lg:rounded-[5rem] lg:p-[1rem]">
            {data?.heroVideo?.muxVideo?.playbackId ? (
              <HLSVideoPlayer
                source={data.heroVideo}
                autoPlay
                muted
                loop
                playsInline
                className="hidden w-full rounded-[2rem] object-cover object-top md:block lg:rounded-[4rem]"
              />
            ) : (
              <Image
                className="hidden w-full rounded-[2rem] md:block lg:rounded-[4rem]"
                src={data?.settings?.language === "en" ? heroAssetDesktopEn : heroAssetDesktopDe}
                quality={100}
                alt="platform preview"
              />
            )}
            <HeroMobilePreview
              key={stegaClean(data?.heroMobileVideo?.playbackId ?? "v12")}
              language={data?.settings?.language}
              video={data?.heroMobileVideo}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
